/**
 * EmbedSnippetCard — mostra à câmara municipal um widget iframe
 * copy-paste que pode ser colado no site oficial deles.
 *
 * Suporta configurações: tema (dark/light), colunas (1/2/3), limite de rotas,
 * altura em px. Actualiza o snippet e a pré-visualização em tempo real.
 *
 * Props:
 *   - slug (string) — municipality_id / slug
 *   - name (string) — nome do concelho (para snippet title)
 *   - api  (string) — não usado (kept for parity)
 *   - C    (object) — paleta do dashboard pai
 */
import React, { useMemo, useState } from 'react';
import { Copy, Check, Code2 } from 'lucide-react';
import { toast } from 'sonner';

const FRONT = typeof window !== 'undefined' ? window.location.origin : '';

export const EmbedSnippetCard = ({ slug, name, C }) => {
  const [theme, setTheme] = useState('dark');
  const [columns, setColumns] = useState(3);
  const [limit, setLimit] = useState(6);
  const [height, setHeight] = useState(640);
  const [showHeader, setShowHeader] = useState(true);
  const [copied, setCopied] = useState(false);

  const embedUrl = useMemo(() => {
    const params = new URLSearchParams({
      theme,
      columns: String(columns),
      limit: String(limit),
      showHeader: showHeader ? '1' : '0',
    });
    return `${FRONT}/embed/m/${slug}?${params.toString()}`;
  }, [slug, theme, columns, limit, showHeader]);

  const snippet = useMemo(() => (
`<!-- Widget Tectur 4×4 · Rotas oficiais de ${name} -->
<iframe
  src="${embedUrl}"
  width="100%"
  height="${height}"
  frameborder="0"
  loading="lazy"
  title="Rotas 4×4 em ${name} · Tectur"
  style="border:0;max-width:100%;background:transparent;"
></iframe>`
  ), [embedUrl, height, name]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success('Snippet copiado para o clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Não foi possível copiar. Selecciona e copia manualmente.');
    }
  };

  return (
    <div
      className="rounded-xl p-4 border"
      style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }}
      data-testid="embed-snippet-card"
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: C.brandDim, border: `1px solid ${C.brand}` }}
        >
          <Code2 className="w-4 h-4" style={{ color: C.brand }} />
        </div>
        <div>
          <div className="text-sm font-bold" style={{ color: C.ink }}>
            Widget Embed
          </div>
          <div className="text-xs" style={{ color: C.inkDim }}>
            Cola no site oficial e mostra as rotas 4×4 do teu concelho em tempo real.
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider block mb-1" style={{ color: C.inkDim }}>Tema</label>
          <div className="flex gap-1 p-1 rounded-md" style={{ backgroundColor: 'rgba(0,0,0,0.35)', border: `1px solid ${C.sandLine}` }}>
            {['dark', 'light'].map((t) => (
              <button
                key={t}
                onClick={() => setTheme(t)}
                data-testid={`embed-theme-${t}`}
                className="flex-1 px-2 py-1 rounded text-[11px] font-bold transition"
                style={{
                  backgroundColor: theme === t ? C.brand : 'transparent',
                  color: theme === t ? '#fff' : C.ink,
                }}
              >
                {t === 'dark' ? '🌙 Dark' : '☀️ Light'}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider block mb-1" style={{ color: C.inkDim }}>Colunas</label>
          <div className="flex gap-1 p-1 rounded-md" style={{ backgroundColor: 'rgba(0,0,0,0.35)', border: `1px solid ${C.sandLine}` }}>
            {[1, 2, 3].map((n) => (
              <button
                key={n}
                onClick={() => setColumns(n)}
                data-testid={`embed-cols-${n}`}
                className="flex-1 px-2 py-1 rounded text-[11px] font-bold transition"
                style={{
                  backgroundColor: columns === n ? C.brand : 'transparent',
                  color: columns === n ? '#fff' : C.ink,
                }}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider block mb-1" style={{ color: C.inkDim }}>Rotas (máx)</label>
          <input
            type="number"
            min="1"
            max="24"
            value={limit}
            onChange={(e) => setLimit(Math.max(1, Math.min(24, parseInt(e.target.value || '6', 10))))}
            data-testid="embed-limit-input"
            className="w-full px-2 py-1 rounded text-xs font-semibold outline-none"
            style={{
              backgroundColor: 'rgba(0,0,0,0.35)', color: C.ink,
              border: `1px solid ${C.sandLine}`,
            }}
          />
        </div>

        <div>
          <label className="text-[10px] font-bold uppercase tracking-wider block mb-1" style={{ color: C.inkDim }}>Altura (px)</label>
          <input
            type="number"
            min="200"
            max="2000"
            step="20"
            value={height}
            onChange={(e) => setHeight(Math.max(200, Math.min(2000, parseInt(e.target.value || '640', 10))))}
            data-testid="embed-height-input"
            className="w-full px-2 py-1 rounded text-xs font-semibold outline-none"
            style={{
              backgroundColor: 'rgba(0,0,0,0.35)', color: C.ink,
              border: `1px solid ${C.sandLine}`,
            }}
          />
        </div>
      </div>

      <label className="flex items-center gap-2 mb-4 cursor-pointer">
        <input
          type="checkbox"
          checked={showHeader}
          onChange={(e) => setShowHeader(e.target.checked)}
          data-testid="embed-header-toggle"
        />
        <span className="text-xs" style={{ color: C.inkDim }}>
          Incluir cabeçalho com o nome do concelho e link &quot;Ver todas&quot;
        </span>
      </label>

      {/* Snippet */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: C.inkDim }}>
            Código &lt;iframe&gt; para colar no teu site
          </label>
          <button
            onClick={copy}
            data-testid="embed-copy-btn"
            className="px-2 py-1 rounded text-[11px] font-bold flex items-center gap-1 transition"
            style={{ backgroundColor: copied ? C.green : C.brand, color: '#fff' }}
          >
            {copied ? <><Check className="w-3 h-3" /> Copiado</> : <><Copy className="w-3 h-3" /> Copiar</>}
          </button>
        </div>
        <textarea
          readOnly
          value={snippet}
          onFocus={(e) => e.target.select()}
          data-testid="embed-snippet-textarea"
          rows={7}
          className="w-full p-2 rounded text-[11px] font-mono resize-none outline-none"
          style={{
            backgroundColor: '#0F0D0B', color: '#F2EBDD',
            border: `1px solid ${C.sandLine}`,
            lineHeight: 1.5,
          }}
        />
      </div>

      {/* Preview */}
      <div>
        <label className="text-[10px] font-bold uppercase tracking-wider block mb-1" style={{ color: C.inkDim }}>
          Pré-visualização
        </label>
        <div
          className="rounded-md overflow-hidden"
          style={{ border: `1px solid ${C.sandLine}`, backgroundColor: theme === 'light' ? '#F5F0E6' : '#0F0D0B' }}
        >
          <iframe
            key={embedUrl}
            src={embedUrl}
            width="100%"
            height={Math.min(height, 500)}
            frameBorder="0"
            title="Pré-visualização"
            data-testid="embed-preview-iframe"
            style={{ border: 0, display: 'block', background: theme === 'light' ? '#F5F0E6' : '#0F0D0B', colorScheme: theme }}
          />
        </div>
      </div>
    </div>
  );
};

export default EmbedSnippetCard;
